import { ReactElement, useEffect, useMemo, useState } from "react";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { Switch } from "../../components/ui/Switch";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { TransferMethod } from "../../../core/models/entities/LogoOptions";
import { isScreenPrintMethod } from "../../../utils/functions/TransferMethodUtil";
import { useErrorToast, useSuccessToast } from "../../components/Toast/Toast";
import { useConfirmationDialog } from "../../components/Dialogs/confirmation-dialog";

interface MethodRow {
  method: TransferMethod;
  /** Which selectable lists the key appears in (logo/numbers/names). */
  lists: string[];
  screenPrint: boolean;
}

function ListBadge({ label }: { label: string }) {
  return (
    <span className="inline-block rounded border border-slate-200 bg-slate-100 text-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide mr-1">
      {label}
    </span>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2 font-medium text-[11px] uppercase tracking-wide">
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-3 py-2 align-middle ${className}`}>{children}</td>;
}

export function MethodsScreen(): ReactElement {
  const { logoRepository, transferMethodConfigRepository } =
    useRepositoriesContext();
  const successToast = useSuccessToast;
  const errorToast = useErrorToast;

  const options = logoRepository.useOptions();
  const blocked = transferMethodConfigRepository.useBlockedMethods();

  // Draft holds lowercase method keys; the server list is the source of truth
  // and is re-synced whenever it loads/refetches.
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const serverBlocked = useMemo(
    () => new Set((blocked.data ?? []).map((k) => k.toLowerCase())),
    [blocked.data],
  );

  useEffect(() => {
    setDraft(new Set(serverBlocked));
  }, [serverBlocked]);

  const rows: MethodRow[] = useMemo(() => {
    if (!options.data) return [];
    const byKey = new Map<string, MethodRow>();
    const sources: [TransferMethod[], string][] = [
      [options.data.transferMethods, "Logoer"],
      [options.data.transferMethodsNumbers, "Numre"],
      [options.data.transferMethodsNames, "Navne"],
    ];
    for (const [methods, label] of sources) {
      for (const m of methods) {
        const existing = byKey.get(m.key.toLowerCase());
        if (existing) {
          if (!existing.lists.includes(label)) existing.lists.push(label);
        } else {
          byKey.set(m.key.toLowerCase(), {
            method: m,
            lists: [label],
            screenPrint: isScreenPrintMethod(m),
          });
        }
      }
    }
    return Array.from(byKey.values()).sort(
      (a, b) =>
        a.method.type.localeCompare(b.method.type) ||
        a.method.displayName.localeCompare(b.method.displayName),
    );
  }, [options.data]);

  // Blocked keys that no longer exist in the method lists (e.g. the method was
  // removed upstream). They are kept on save so nothing is silently dropped.
  const unknownBlockedKeys = useMemo(() => {
    const known = new Set(rows.map((r) => r.method.key.toLowerCase()));
    return Array.from(draft).filter((k) => !known.has(k));
  }, [rows, draft]);

  const hasChanges = useMemo(() => {
    if (draft.size !== serverBlocked.size) return true;
    return Array.from(draft).some((k) => !serverBlocked.has(k));
  }, [draft, serverBlocked]);

  function toggle(key: string) {
    const k = key.toLowerCase();
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  async function performSave() {
    if (saving) return;
    setSaving(true);
    try {
      await transferMethodConfigRepository.update(Array.from(draft));
      successToast("Metode-indstillinger gemt.");
    } catch (e) {
      errorToast(
        e instanceof Error
          ? `Kunne ikke gemme: ${e.message}`
          : "Kunne ikke gemme",
      );
    } finally {
      setSaving(false);
    }
  }

  const blockedCount = draft.size - unknownBlockedKeys.length;
  const { component: confirmSaveDialog, open: openConfirmSave } =
    useConfirmationDialog(
      `Vil du gemme metode-indstillingerne?\n\n` +
        `${blockedCount} metode(r) bliver skjult i kundernes dropdown-menuer ved oprettelse og kloning. ` +
        `Eksisterende logoer beholder deres metode. NAV-import påvirkes ikke.`,
      () => {
        performSave();
      },
    );

  const isLoading = options.isLoading || blocked.isLoading;
  const isError = options.isError || blocked.isError;

  return (
    <div className="p-6 max-w-[1100px] mx-auto">
      {confirmSaveDialog}
      <JetSportBox>
        <BoxHeader2 className="border-b">
          <div className="flex items-center justify-between w-full gap-4">
            <BoxHeaderTypography>Metoder</BoxHeaderTypography>
            <div className="flex items-center gap-2">
              <SecondaryButton
                onClick={() => setDraft(new Set(serverBlocked))}
                disabled={!hasChanges || saving}
              >
                Nulstil
              </SecondaryButton>
              <PrimaryButton
                onClick={openConfirmSave}
                disabled={!hasChanges || saving}
              >
                {saving ? "Gemmer…" : "Gem ændringer"}
              </PrimaryButton>
            </div>
          </div>
        </BoxHeader2>
        <BoxContent>
          <p className="text-sm text-slate-600 mb-4">
            Vælg hvilke transfermetoder kunder selv kan vælge, når de opretter
            eller kloner logoer, numre og navne. Slukkede metoder skjules i
            dropdown-menuerne, men kan stadig oprettes via NAV-import, og
            eksisterende logoer beholder deres metode. Ændringer slår igennem
            hos kunder ved næste genindlæsning af KitMaker.
          </p>

          {isLoading && (
            <div className="py-10 text-center text-slate-500">Indlæser…</div>
          )}
          {isError && !isLoading && (
            <div className="py-10 text-center text-red-600">
              Kunne ikke hente metoder. Prøv at genindlæse siden.
            </div>
          )}

          {!isLoading && !isError && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-slate-50 text-slate-600 border-b">
                  <tr>
                    <Th>Kan vælges</Th>
                    <Th>Metode</Th>
                    <Th>Type</Th>
                    <Th>Nøgle</Th>
                    <Th>Bruges i</Th>
                    <Th>Størrelse</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const key = row.method.key.toLowerCase();
                    const isBlocked = row.screenPrint || draft.has(key);
                    return (
                      <tr
                        key={row.method.typeAndKey}
                        className={`border-b last:border-b-0 ${
                          isBlocked ? "bg-slate-50 text-slate-400" : ""
                        }`}
                      >
                        <Td>
                          <Switch
                            checked={!isBlocked}
                            disabled={row.screenPrint || saving}
                            onCheckedChange={() => toggle(row.method.key)}
                          />
                        </Td>
                        <Td className="font-medium">
                          {row.method.displayName}
                          {row.screenPrint && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">
                              Altid skjult (kun NAV-import)
                            </span>
                          )}
                        </Td>
                        <Td>{row.method.type}</Td>
                        <Td className="font-mono text-xs">{row.method.key}</Td>
                        <Td>
                          {row.lists.map((l) => (
                            <ListBadge key={l} label={l} />
                          ))}
                        </Td>
                        <Td>{row.method.size}</Td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-8 text-center text-slate-500"
                      >
                        Ingen metoder fundet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {unknownBlockedKeys.length > 0 && (
            <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              Følgende blokerede nøgler findes ikke i metode-listen lige nu og
              bevares uændret: {unknownBlockedKeys.join(", ")}
            </div>
          )}
        </BoxContent>
      </JetSportBox>
    </div>
  );
}
